function productRecommendations() {
  return {
    products: [],
    loading: true,
    error: null,

    async init() {
      const url = this.$el.dataset.url;
      if (!url) {
        this.error = 'No recommendation URL provided';
        this.loading = false;
        return;
      }

      try {
        const data = await this.fetchRecommendations(url);

        // Map the API response to our component structure
        this.products = data.products.map(product => ({
          id: product.id,
          title: product.title,
          handle: product.handle,
          vendor: product.vendor,
          price: product.price,
          compare_at_price: product.compare_at_price,
          featured_image: product.featured_image,
          url: product.url,
          tags: product.tags || [],
          // Handle metafields if they exist in the response
          metafields: product.metafields || {}
        }));
        
      } catch (err) {
        this.error = `Failed to load recommendations: ${err.message}`;
        console.error('Product recommendations error:', err);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Fetch recommendations with retry. Shopify's product recommendations API
     * returns a transient 503 (and occasionally 429) while it is still
     * computing results for a product, which is common on first load. Retry a
     * few times with a short backoff before surfacing an error.
     * @param {string} url - The recommendations endpoint URL
     * @param {number} [attempts] - Maximum number of attempts
     * @returns {promise<object>} The parsed recommendations payload
     */
    async fetchRecommendations(url, attempts = 3) {
      let lastError;

      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          const response = await fetch(url);
          if (response.ok) {
            return await response.json();
          }

          lastError = new Error(`HTTP error! status: ${response.status}`);

          // Only retry transient server states; bail immediately on others
          // (e.g. a 404 for a bad product id will never succeed).
          const isRetryable = response.status === 429 || response.status >= 500;
          if (!isRetryable) {
            break;
          }
        } catch (err) {
          // Network error — worth another try.
          lastError = err;
        }

        // Backoff before the next attempt (skip the wait after the last try).
        if (attempt < attempts - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }

      throw lastError;
    },

    formatPrice(price) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(price / 100);
    },

    /**
     * Build a fallback src at a resolution that stays crisp on Retina.
     * The recommendation cards render at 25vw (desktop) / 50vw (mobile),
     * so a flat 400px source was ~half-resolution on 2x displays.
     * @param {string} imageUrl - Shopify CDN image URL from the recommendations API
     * @returns {string} URL with a width parameter appended
     */
    processImageUrl(imageUrl) {
      if (!imageUrl) return null;
      const separator = imageUrl.includes('?') ? '&' : '?';
      return `${imageUrl}${separator}width=800`;
    },

    /**
     * Build a responsive srcset so the browser picks a crisp candidate,
     * matching the standard collection grid (which renders via the image snippet).
     * @param {string} imageUrl - Shopify CDN image URL from the recommendations API
     * @returns {string} comma-separated srcset candidates
     */
    buildSrcset(imageUrl) {
      if (!imageUrl) return null;
      const separator = imageUrl.includes('?') ? '&' : '?';
      const widths = [300, 400, 600, 800, 1200];
      return widths.map((w) => `${imageUrl}${separator}width=${w} ${w}w`).join(', ');
    },

    getDisplayLabel(product) {
      // Try to get featured collection from metafields, fallback to vendor
      if (product.metafields && product.metafields.custom && product.metafields.custom.featured_collection) {
        return product.metafields.custom.featured_collection.value.title;
      }
      return product.vendor;
    }
  }
}